import { HttpModel } from '@/Shared/domain'
import { createQueryString } from '@/Shared/infrastructure'
import { checkBlockedEndpoint } from '@/Shared/infrastructure/endpoints'
import { useCallback, useEffect, useRef, useState } from 'react'

interface CheckEndpointLimitRateData {
  [key: string]: string
}

interface UseRateLimitedCountdownParams {
  messageTemplate: string
  params: CheckEndpointLimitRateData
  needFakeCountdown?: boolean
}

export const useRateLimitedCountdown = ({
  messageTemplate,
  params,
  needFakeCountdown = false,
}: UseRateLimitedCountdownParams) => {
  const [isRunning, setIsRunning] = useState(false)
  const [isBlocked, setIsBlocked] = useState(false)
  const [formattedMessage, setFormattedMessage] = useState(messageTemplate)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const setCountdownCookie = useCallback((value: number, minutes: number) => {
    const now = new Date()
    const utcTime = now.getTime() + minutes * 60 * 1000
    const expires = new Date(utcTime).toUTCString()

    const currentPath = window.location.pathname

    document.cookie = `isFirstClick=${value}; expires=${expires}; path=${currentPath}`
  }, [])

  const getCountdownCookie = useCallback((): string | boolean => {
    const nameEQ = 'isFirstClick='

    const cookies = document.cookie.split(';')

    for (let c of cookies) {
      c = c.trim()

      if (c.startsWith(nameEQ)) {
        const cookieValue = c.substring(nameEQ.length)
        return cookieValue
      }
    }
    return false
  }, [])

  const [isFirstClick, setIsFirstClick] = useState<boolean>(!getCountdownCookie())
  const [isFirstClickCountEnded, setIsFirstClickCountEnded] = useState<boolean>(false)
  const [isFakeRunning, setIsFakeRunning] = useState(isFirstClickCountEnded ? false : true)
  const [secondsLeft, setSecondsLeft] = useState(isFakeRunning ? getCountdownCookie() ? Number(getCountdownCookie()) : 0 : 0)

  // Helper to format mm:ss
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0')
    const secs = (seconds % 60).toString().padStart(2, '0')
    return `${minutes}:${secs}`
  }

  // Get remaining seconds from api
  const checkEndpointRateLimit = useCallback(async (): Promise<HttpModel<boolean>> => {
    try {
      const queryString = createQueryString(params)
      let uri: string = `${import.meta.env.VITE_GATEWAY_URL}/${checkBlockedEndpoint}${queryString}`
      const res = await fetch(uri, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const response: HttpModel<boolean> = await res.json()
      return response
    } catch (err) {
      throw err
    }
  }, [params])

  // Start the countdown
  const startCountdown = useCallback(async () => {
    const response = await checkEndpointRateLimit()
    if (response.data) {
      const remainingSeconds = +response.data < 0 ? 0 : +response.data
      if (remainingSeconds > 0) {
        setSecondsLeft(remainingSeconds)
        setIsRunning(true)
        setIsBlocked(true)
      }
    }
  }, [checkEndpointRateLimit])

  // Stop the countdown
  const stopCountdown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setIsRunning(false)
    setIsBlocked(false)
  }, [])

  // Fake countdown logic
  const startFakeCountdown = useCallback((fakeSeconds: number) => {
    setSecondsLeft(fakeSeconds)
    setIsFakeRunning(true)
  }, [])

  // Show message
  const showCountdown = useCallback(() => {
    setIsBlocked(true)
  }, [])

  // Check if the form should be blocked
  const shouldBlockForm = useCallback(async (): Promise<boolean> => {
    try {
      const response = await checkEndpointRateLimit();
      // Check if data is a number and greater than 0
      if (typeof response.data === 'number' && response.data > 0) {
        return true;
      }
      return false;
    } catch (err) {
      return false; // Default to not blocking the form in case of an error
    }
  }, [checkEndpointRateLimit]);

  // Update the formatted message
  useEffect(() => {
    setFormattedMessage(
      messageTemplate.replace('{{timeLeft}}', formatTime(secondsLeft))
    )
  }, [secondsLeft, messageTemplate])

  // Countdown logic
  useEffect(() => {
    if (isRunning || isFakeRunning) {
      intervalRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current!)
            setIsRunning(false)
            setIsFakeRunning(false)
            setIsFirstClick(false)
            setIsFirstClickCountEnded(true)
            setIsBlocked(false)
            if (needFakeCountdown) setCountdownCookie(0, 3)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } else {
      return
    }

    return () => clearInterval(intervalRef.current!)
  }, [isRunning, isFakeRunning])

  // Handle browser visibility changes
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!isFakeRunning) {
        if (document.visibilityState === 'visible') {
          const response = await checkEndpointRateLimit()
          if (response.data) {
            const remainingSeconds = +response.data <= 0 ? 0 : +response.data
            setSecondsLeft(remainingSeconds)
            if (remainingSeconds > 0) {
              setIsRunning(true)
              setIsBlocked(true)
            }
          } else {
            setIsRunning(false)
            setIsBlocked(false)
          }
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [isFakeRunning])

  useEffect(() => {
    if (needFakeCountdown) {
      if (!isFirstClick && !isFirstClickCountEnded) {
        setCountdownCookie(secondsLeft, 3)
      }
    }
  }, [isFirstClick, secondsLeft])

  // Fetch remaining time on first render
  useEffect(() => {
    if (needFakeCountdown) {
      const cookieValue = getCountdownCookie()
      if (cookieValue && !isNaN(Number(cookieValue)) && Number(cookieValue) > 0) {
        startFakeCountdown(Number(cookieValue))
        showCountdown()
      } else if (cookieValue && !isNaN(Number(cookieValue)) && Number(cookieValue) <= 0) {
        startCountdown()
      } else {
        startFakeCountdown(30)
      }
    } else {
      startCountdown()
    }
  }, [])

  return {
    formattedMessage, // Real-time message with updated mm:ss
    startCountdown, // Method to initialize the countdown
    stopCountdown, // Method to stop the countdown
    startFakeCountdown, // Method to start a fake countdown
    isBlocked, // Whether the form should be blocked
    shouldBlockForm, // Check if form should be blocked
    showCountdown, // Show the countdown message
    isFirstClick, // Whether it's the first click for fake countdown purposes
    setIsFirstClick // Set the first click state
  }
}